import { zoomStepMoves, type RenderDivisor, type Size } from "odyssey-shared";
import { parseZoomStep, type ZoomStep } from "../settings.ts";
import { say } from "../../words.ts";

/**
 * The world zoom setting as Settings offers it (P1.9-T14; ADR-061, "world zoom 2.5 ships where it fits"):
 * three steps from the window's default, each one place through the zooms allowed there (`stepWorldZoom`),
 * worded by the language files. A choice's value is its step as `settings.ts` keeps it in the store, so the
 * segmented choice, the store and the page's `WorldZoom` all read one format.
 */

/**
 * A choice's value: its step as the store keeps it.
 */
export type ZoomChoiceValue = "-1" | "0" | "1";

/**
 * One choice: its step and its label.
 */
export interface ZoomChoice {
  readonly step: ZoomStep;
  readonly label: string;
}

/**
 * The choices, in the order Settings offers them: Farther, Default and Closer.
 *
 * @returns The choices.
 */
export function zoomChoices(): readonly ZoomChoice[] {
  return [
    { step: -1, label: say("settings.zoomChoices.farther") },
    { step: 0, label: say("settings.zoomChoices.default") },
    { step: 1, label: say("settings.zoomChoices.closer") },
  ];
}

/**
 * A step as a choice's value.
 *
 * @param step The step.
 * @returns The value, the step as the store keeps it.
 * @example
 * zoomChoiceValue(-1);
 * // "-1"
 */
export function zoomChoiceValue(step: ZoomStep): ZoomChoiceValue {
  return step === -1 ? "-1" : step === 1 ? "1" : "0";
}

/**
 * A choice's value as its step.
 *
 * @param value The value.
 * @returns The step, read as the store's text is read.
 */
export function zoomChoiceStep(value: ZoomChoiceValue): ZoomStep {
  return parseZoomStep(value);
}

/**
 * The choices as the segmented choice takes them at a render resolution (ADR-093 decision 10, note 12): a step
 * that gives the same zoom as Default there is disabled, since above Full a step is held where it would show more
 * of the world than Full shows at that step.
 *
 * @param device The window's device size, or null on a page with no render resolution, where every step moves.
 * @param divisor The pending render divisor.
 * @returns Each choice's value and label, disabled when the step would change nothing.
 * @example
 * zoomOptions({ width: 2560, height: 1496 }, 2).map((option) => option.disabled);
 * // [true, false, false]
 */
export function zoomOptions(device: Size | null, divisor: RenderDivisor): readonly { readonly value: ZoomChoiceValue; readonly label: string; readonly disabled: boolean }[] {
  return zoomChoices().map((choice) => ({ value: zoomChoiceValue(choice.step), label: choice.label, disabled: device !== null && !zoomStepMoves(device, divisor, choice.step) }));
}

/**
 * The World zoom row's note: which steps do nothing at the pending render resolution, so nobody presses a choice
 * that changes nothing without being told. A draft for the author.
 *
 * @param device The window's device size, or null on a page with no render resolution.
 * @param divisor The pending render divisor.
 * @returns The sentence, or null when every step moves.
 * @example
 * zoomNote({ width: 2560, height: 1496 }, 2);
 * // "Farther is the same as Default at this render resolution."
 */
export function zoomNote(device: Size | null, divisor: RenderDivisor): string | null {
  const still = zoomOptions(device, divisor)
    .filter((option) => option.disabled)
    .map((option) => option.label);
  return still.length === 0 ? null : sameAsDefault(still);
}

/**
 * The sentence that says which choices change nothing.
 *
 * @param still The labels of the choices that are the same as Default, at least one.
 * @returns The sentence.
 */
export function sameAsDefault(still: readonly string[]): string {
  const choices = still.reduce((first, second) => say("settings.zoomBoth", { first, second }));
  return say("settings.zoomSame", { count: still.length, choices });
}
